import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import { Empty } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { IconShell } from 'components/atoms/icon-shell';
import { SpaceIcon } from 'components/atoms/space-icon';
import { ProjectTemplateSpace } from 'data/templates/projects';
import { MenuGroupTitle } from './menu-group-title';
import { NewSpace } from './new-space';
import { Menu } from './styled';
import { SpaceSubMenuItem } from './sub-menu-item';
import { generateTabMenuKey } from './utils';

export const ExpandIconContainer = styled.div`
  position: absolute;
  margin-left: calc(-1 * var(--spacing-06));
  top: var(--spacing-02);
`;

export const MenuGroupItem = styled(Menu.ItemGroup)`
  .ant-menu-item-group-title {
    height: auto;
    padding: 0 var(--spacing-03) 0 var(--spacing-05);
  }
`;

interface MenuGroupProps {
  projectSpaces: ProjectTemplateSpace[];
  spaceId: string;
  spaceTabId: string;
  title: string;
  onAdd: (isPrivate: boolean) => void;
  isPrivate: boolean;
  isLoading?: boolean | undefined;
}

export const MenuGroup: FC<MenuGroupProps> = ({
  projectSpaces,
  spaceId,
  spaceTabId,
  title,
  onAdd,
  isPrivate,
  isLoading,
}) => {
  const [collapse, setCollapse] = useState(true);
  const defaultOpenKeys = useMemo(
    () => projectSpaces.map((space) => space.spaceId.toString()),
    [projectSpaces],
  );
  const [openKeys, setOpenKeys] = useState(defaultOpenKeys);

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const isNoProjectSpaces = useMemo(
    () => projectSpaces.length === 0,
    [projectSpaces],
  );

  const onOpenChangeHandler = useCallback((key) => {
    if (key.length === 0) {
      setCollapse(false);

      return;
    }
    setCollapse(true);
    onOpenChange(key);
  }, []);

  return (
    <Menu
      mode="inline"
      defaultOpenKeys={defaultOpenKeys}
      openKeys={collapse ? openKeys : []}
      onOpenChange={(key) => onOpenChangeHandler(key)}
      defaultSelectedKeys={[spaceId, generateTabMenuKey(spaceId, spaceTabId)]}
      selectedKeys={[spaceId, generateTabMenuKey(spaceId, spaceTabId)]}
      expandIcon={({ isOpen }) => {
        return (
          <ExpandIconContainer>
            {isOpen ? <ChevronDown16 /> : <ChevronRight16 />}
          </ExpandIconContainer>
        );
      }}
    >
      <MenuGroupItem
        title={
          <MenuGroupTitle
            title={title}
            onSpaceAdd={onAdd}
            collapseAll={() => {
              setCollapse((collapseValue) => !collapseValue);
              setOpenKeys(defaultOpenKeys);
            }}
            collapse={collapse}
            isPrivate={isPrivate}
          />
        }
        data-cy={`projects.spaces.${isPrivate ? 'private' : 'shared'}`}
      />

      {isNoProjectSpaces ? (
        <Empty>
          <FormattedMessage
            id={`space.${isPrivate ? 'private' : 'shared'}.empty`}
          />
        </Empty>
      ) : null}
      {!isLoading && !isNoProjectSpaces
        ? projectSpaces.map((space) => (
            <>
              {space.isNew ? (
                <Menu.Item
                  key={space.spaceId}
                  icon={
                    <IconShell
                      size="small"
                      icon={<SpaceIcon name={space.spaceName} />}
                    />
                  }
                >
                  <NewSpace spaceId={Number(space.spaceId)} />
                </Menu.Item>
              ) : (
                <SpaceSubMenuItem
                  key={space.spaceId}
                  space={space}
                  setOpenKeys={(keys) => setOpenKeys(() => [...keys])}
                  isPrivate={isPrivate}
                />
              )}
            </>
          ))
        : null}
    </Menu>
  );
};
